import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { landingAPI } from '../lib/api';
import CaptchaWidget from './CaptchaWidget';

// Waiting List capture modal for the LIVE site (Private Community Pro).
// Opens on any click of a link whose href ends with #waiting-list (or #waitlist),
// or on a window 'aurex:open-waitlist' event — so the operator can wire the hero
// "Notify Me!" button (or any nav/footer link) to it from the CMS by setting its
// URL to #waiting-list. Same fields as the old Landing Page waitlist
// (first_name / last_name / email) + reCAPTCHA, posting to the existing
// landingAPI.subscribe -> POST /api/public/landing-subscribe (stored in
// landing_subscribers, shown in CMS -> /admin/landing-subscribers).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function WaitingListModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '' });
  const [errors, setErrors] = useState({});
  const [captcha, setCaptcha] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = useCallback(() => {
    setForm({ first_name: '', last_name: '', email: '' });
    setErrors({});
    setCaptcha('');
    setSubmitting(false);
    setDone(false);
  }, []);

  const close = useCallback(() => { setOpen(false); }, []);

  // Trigger: intercept #waiting-list links anywhere on the public site + a custom event.
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href$="#waiting-list"], a[href$="#waitlist"]');
      if (a) { e.preventDefault(); reset(); setOpen(true); }
    };
    const onEvt = () => { reset(); setOpen(true); };
    document.addEventListener('click', onClick);
    window.addEventListener('aurex:open-waitlist', onEvt);
    return () => { document.removeEventListener('click', onClick); window.removeEventListener('aurex:open-waitlist', onEvt); };
  }, [reset]);

  // Escape to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, close]);

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Enter your name';
    if (!form.last_name.trim()) e.last_name = 'Enter your last name';
    if (!form.email.trim()) e.email = 'Enter your email';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!captcha) e.captcha = 'Please confirm you are not a robot';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await landingAPI.subscribe({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        captcha_token: captcha,
      });
      const already = res && res.data && /already/i.test(res.data.message || '');
      setDone(true);
      toast.success(already ? "You're already on the list!" : "You're on the waiting list!");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const accent = 'var(--color-accent, #73af5f)';
  const inputBase = {
    width: '100%', height: 50, padding: '0 16px', borderRadius: 12,
    background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15,
    fontFamily: "'Inter', system-ui, sans-serif", outline: 'none',
    border: '1px solid rgba(255,255,255,0.14)', transition: 'border-color .2s ease',
  };
  const errStyle = { color: '#f59a9a', fontSize: 12.5, margin: '6px 2px 0' };
  const field = (name, placeholder, type = 'text') => (
    <div>
      <input
        type={type} placeholder={placeholder} value={form[name]}
        onChange={(e) => { setForm(p => ({ ...p, [name]: e.target.value })); if (errors[name]) setErrors(p => ({ ...p, [name]: undefined })); }}
        style={{ ...inputBase, borderColor: errors[name] ? '#c4505e' : 'rgba(255,255,255,0.14)' }}
        onFocus={(e) => { if (!errors[name]) e.target.style.borderColor = accent; }}
        onBlur={(e) => { if (!errors[name]) e.target.style.borderColor = 'rgba(255,255,255,0.14)'; }}
        data-testid={`waitlist-${name}`}
      />
      {errors[name] && <p style={errStyle}>{errors[name]}</p>}
    </div>
  );

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label="Waiting List"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 20,
        background: 'rgba(6,10,20,0.72)', backdropFilter: 'blur(6px)',
        animation: 'pcp-fade .25s ease',
      }}
      data-testid="waitlist-modal"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: 460,
          background: 'linear-gradient(180deg,#111a2b,#0d1524)',
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.7)', padding: '38px 34px 34px',
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <button onClick={close} aria-label="Close" data-testid="waitlist-close"
          style={{ position: 'absolute', top: 16, right: 16, width: 34, height: 34, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', cursor: 'pointer' }}>
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="text-center" style={{ padding: '10px 0 6px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 60, height: 60, borderRadius: 999, background: accent, marginBottom: 18 }}>
              <Check className="w-7 h-7" style={{ color: 'var(--accent-ink)' }} />
            </span>
            <h3 style={{ margin: '0 0 8px', fontFamily: "'Sora', sans-serif", fontSize: 24, fontWeight: 800, color: '#fff' }}>You're on the list!</h3>
            <p style={{ margin: '0 auto 24px', maxWidth: 320, fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,0.6)' }}>Thanks for your interest. We will send you an invitation once a new slot is available.</p>
            <button onClick={close} className="pcpb pcpb--green" data-contrast="dark" style={{ width: '100%', justifyContent: 'center' }} data-testid="waitlist-done-close">Done</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 13px 6px 6px', borderRadius: 999, background: 'color-mix(in srgb, var(--color-accent) 14%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 34%, transparent)', marginBottom: 16 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: accent }} />
              <span style={{ fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--accent-soft)' }}>Waiting List</span>
            </div>
            <h3 style={{ margin: '0 0 8px', fontFamily: "'Sora', sans-serif", fontSize: 26, fontWeight: 800, letterSpacing: '-.01em', color: '#fff' }}>Join the waiting list</h3>
            <p style={{ margin: '0 0 22px', fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,0.62)' }}>Sign up to get exclusive early access before the grand opening.</p>

            <form onSubmit={submit} noValidate className="space-y-3.5">
              {field('first_name', 'Name')}
              {field('last_name', 'Last Name')}
              {field('email', 'Email', 'email')}
              <div>
                <div className="flex justify-center" style={{ marginTop: 4 }}>
                  <CaptchaWidget onChange={(t) => { setCaptcha(t); if (t && errors.captcha) setErrors(p => ({ ...p, captcha: undefined })); }} testId="waitlist-captcha" theme="dark" />
                </div>
                {errors.captcha && <p style={{ ...errStyle, textAlign: 'center' }}>{errors.captcha}</p>}
              </div>
              <button type="submit" disabled={submitting} className="pcpb pcpb--green" data-contrast="dark" style={{ width: '100%', justifyContent: 'center', height: 52, marginTop: 6, fontWeight: 700, letterSpacing: '.02em' }} data-testid="waitlist-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
